import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from 'src/shared/api/axios';

type WbsOption = { id: string; code: string; name: string; label: string; orderIndex: number; };

// "1.2.10" vs "1.2.2" 같은 계층코드 자연 정렬
function cmpHierCode(a: string, b: string) {
    return (a ?? '').localeCompare(b ?? '', 'en', { numeric: true, sensitivity: 'base' });
}

async function fetchWbsOptions(projectId: string): Promise<WbsOption[]> {
    const res = await api.get(`/projects/${projectId}/wbs/lists/current`);

    // 백엔드 응답: { ok: true, data: { list, items } }
    const items = res.data?.data?.items ?? [];

    // Task/Risk에 들어가야 하는 값은 "WbsNode.id" -> item.nodeId 사용
    // nodeId 중복 제거 + isDeleted 제외
    const map = new Map<
        string,
        { nodeId: string; code: string; name: string; orderIndex: number }
    >();

    for (const it of items) {
        if (!it) continue;
        if (it.isDeleted) continue;

        const nodeId = it.nodeId as string | undefined;
        if (!nodeId) continue;

        const code = (it.code ?? '').toString();
        const name = (it.name ?? '').toString();
        const orderIndex = typeof it.orderIndex === 'number' ? it.orderIndex : 0;

        // 같은 nodeIdㅏ 여러 번 나오면 "더 좋은 값"으로 채택"
        const prev = map.get(nodeId);
        if (!prev) {
            map.set(nodeId, { nodeId, code, name, orderIndex });
        } else {
            // code/name이 비어있던 경우 채우기, orderIndex는 더 작은 값 우선
            map.set(nodeId, {
                nodeId,
                code: prev.code || code,
                name: prev.name || name,
                orderIndex: Math.min(prev.orderIndex ?? 0, orderIndex ?? 0),
            });
        }
    }

    const rows = Array.from(map.values());

    rows.sort((a, b) => {
        const c = cmpHierCode(a.code, b.code);
        if (c !== 0) return c;

        const oi = (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        if (oi !== 0) return oi;

        return (a.name ?? '').localeCompare(b.name ?? '', 'ko');
    });

    return rows.map((r) => {
        const code = r.code?.trim() ?? '';
        const name = r.name?.trim() ?? '';
        const label = code && name ? `${code} ${name}` : (code || name || '(unnamed)');
        return {
            id: r.nodeId,
            code,
            name,
            label,
            orderIndex: r.orderIndex ?? 0,
        };
    });
}

export function useWbsOptions(projectId: string | null, enabled = true) {
    const q = useQuery({
        queryKey: ['wbs-options', projectId],
        enabled: enabled && !!projectId,
        queryFn: () => fetchWbsOptions(projectId!),
        staleTime: 60_000,
    });

    const options = useMemo(() => q.data ?? [], [q.data]);

    return { ...q, options };
}
